import { INSURIFY_SAVINGS_SHORT } from "./partnerClaims";
// SEO long-form guide pages — drive programmatic content + internal linking.
export const GUIDES = [
  {
    slug: "best-auto-loan-lenders",
    title: "Best Auto Loan Lenders for 2026",
    h1: "The Best Auto Loan Lenders in 2026 (Compared)",
    description:
      "Our 2026 ranking of the top auto loan lenders for new cars, used cars, and refinance — by rate, approval odds, and customer satisfaction.",
    category: "Loans",
    read_time: "9 min read",
    related_tool: "auto-loan-calculator",
    sections: [
      {
        h2: "How we ranked the top auto lenders",
        body: "We evaluated lenders on five factors that actually matter to borrowers: average APR by credit tier, approval rate for non-prime borrowers, soft credit pull availability, customer service satisfaction (J.D. Power + Trustpilot), and funding speed. Banks with mandatory dealer financing or branch visits were ranked lower for convenience.",
      },
      {
        h2: "The top 5 auto lenders right now",
        body: "1. **LightStream** — best for excellent credit (700+ FICO), low rates, fully online.\n\n2. **myAutoLoan** — best comparison marketplace; one form, up to four offers.\n\n3. **Autopay** — best for refinance; competitive rates and quick funding.\n\n4. **PenFed Credit Union** — best credit-union option for 660+ FICO.\n\n5. **Capital One Auto Navigator** — best for pre-qualification with no credit-score impact.",
      },
      {
        h2: "What APR can you realistically get?",
        body: "Average new-car APRs by FICO band (Q4 2025):\n\n• 781+ → 5.2% APR\n• 661–780 → 6.8%\n• 601–660 → 9.6%\n• 501–600 → 13.2%\n• Below 500 → 15.8%+\n\nUsed cars typically run 1–3 percentage points higher than new.",
      },
      {
        h2: "Pre-approval beats dealer financing — every time",
        body: "Walk in pre-approved from at least 2 lenders and the dealer's F&I office has to compete. Without pre-approval, the average buyer pays 1.4 percentage points more in markup. Get your pre-approval first; let the dealer try to beat it.", // noqa: partner-count
      },
    ],
    faqs: [
      {
        q: "Will checking auto loan rates hurt my credit?",
        a: "Most lenders offer soft pull pre-qualification that doesn't impact your score. A hard pull only happens when you formally accept an offer. Multiple hard pulls within a 14-day window count as a single inquiry.",
      },
      {
        q: "What credit score do I need for an auto loan?",
        a: "You can get approved with scores as low as 500, but the best rates require 700+. Below 600, expect APRs of 13%+ and consider a co-signer.",
      },
      {
        q: "Should I get a loan from a bank, credit union, or dealer?",
        a: "Credit unions usually offer the lowest rates if you qualify for membership. Online lenders are fastest and most convenient. Dealer financing is convenient but often costs more — only use it if they beat your pre-approval.",
      },
    ],
  },
  {
    slug: "how-to-lower-car-payment",
    title: "How to Lower Your Car Payment (7 Real Ways)",
    h1: "How to Lower Your Car Payment in 2026",
    description:
      "Seven proven ways to cut your monthly car payment — from refinancing to extending your term, with the math on each.",
    category: "Loans",
    read_time: "7 min read",
    related_tool: "auto-loan-refinance-calculator",
    sections: [
      {
        h2: "1. Refinance your loan (biggest win)",
        body: "If your credit has improved or rates have dropped 1%+ since you bought, refinancing is the fastest way to cut your payment. A $28,000 loan refinanced from 9% to 6% with 48 months left saves ~$115/month.",
      },
      {
        h2: "2. Extend your loan term",
        body: "Going from 48 to 72 months lowers your monthly payment by ~25%, but increases total interest. Use this only if cash flow is genuinely tight — then refinance again later.",
      },
      {
        h2: "3. Trade in for a cheaper car",
        body: "If you're underwater (owe more than the car is worth), trading down is hard. If you have positive equity, downgrading can drop your payment $150–$400/month.",
      },
      {
        h2: "4. Make a one-time principal payment",
        body: "Some lenders allow re-amortization after a lump-sum principal payment — they recalculate your monthly payment based on the new balance.",
      },
      {
        h2: "5. Switch insurance providers",
        body: `${INSURIFY_SAVINGS_SHORT} CarSavr compares top insurers in about 2 minutes.`,
      },
      {
        h2: "6. Drop unnecessary add-ons",
        body: "Audit any GAP, extended warranty, paint protection, or service contracts rolled into your loan. Many can be cancelled pro rata for a partial refund applied to your loan balance.",
      },
      {
        h2: "7. Negotiate with your lender directly",
        body: "If you're in genuine hardship, call your lender. Many will defer a payment, modify the loan, or extend the term without a hard credit pull.",
      },
    ],
    faqs: [
      {
        q: "How much can refinancing actually save me?",
        a: "Borrowers who refinance an average $25k loan save $98/month on average — about $1,176/year. The exact number depends on credit improvement and rate drop.",
      },
      {
        q: "Will extending my term hurt my credit?",
        a: "No — extending the term is a refinance, which is a soft pull during shopping and a hard pull when you accept. It does not damage your score long-term and may help by lowering utilization.",
      },
    ],
  },
  {
    slug: "lease-vs-buy",
    title: "Lease vs. Buy: Which Is Right for You?",
    h1: "Lease vs. Buy a Car: The Honest 2026 Breakdown",
    description:
      "Should you lease or buy? Real math on total cost, flexibility, and the 3 driver profiles for each option.",
    category: "Buying",
    read_time: "8 min read",
    related_tool: "car-affordability-calculator",
    sections: [
      {
        h2: "The 30-second answer",
        body: "Lease if: you want a new car every 3 years, drive under 12,000 miles/year, and value lower monthly payments. Buy if: you drive a lot, keep cars long, want to build equity, or plan to customize.",
      },
      {
        h2: "The real math — 5-year total cost",
        body: "Same $35,000 vehicle, 5 years:\n\n• **Buy**: ~$42,000 total (loan + interest + maintenance) — but you own a $14k asset at the end.\n• **Lease + lease + lease**: ~$36,000 in payments — but you own nothing.\n• **Net cost of ownership**: Buying typically wins by $5–8k over 5 years if you keep the car.",
      },
      {
        h2: "When leasing actually wins",
        body: "1. You write off the lease as a business expense.\n2. You don't want to deal with selling/trading.\n3. You'd otherwise buy a luxury car you can't comfortably afford.\n4. Manufacturer subvented leases (factory discounts on residuals) can beat buying at certain trim levels.",
      },
      {
        h2: "Lease buyout — the underrated play",
        body: "When your lease ends, you have an option to buy the car at the predetermined residual value. If the residual is below current market value (common in 2022–2024 leases), buying out and immediately selling can net $2k–$8k.",
      },
    ],
    faqs: [
      {
        q: "Can I refinance a leased car?",
        a: "Not directly — but you can finance a lease buyout at end-of-lease (or earlier with lender approval), effectively converting the lease into a loan with a better rate.",
      },
      {
        q: "What's the average lease payment in 2026?",
        a: "About $560/month nationwide, vs. $730/month for an average new-car loan payment.",
      },
    ],
  },
  {
    slug: "car-loan-rates",
    title: "Current Car Loan Rates by Credit Score",
    h1: "Current Auto Loan Rates by Credit Score (2026)",
    description:
      "Live ranges for new and used car loan APRs by FICO band, plus how to lock the lowest rate you qualify for.",
    category: "Loans",
    read_time: "5 min read",
    related_tool: "auto-loan-calculator",
    sections: [
      {
        h2: "Average APR by credit score (Q4 2025)",
        body: "**New car APRs:**\n• 781+ → 5.2%\n• 661–780 → 6.8%\n• 601–660 → 9.6%\n• 501–600 → 13.2%\n• Below 500 → 15.8%+\n\n**Used car APRs**: add 1–3 percentage points to each band.",
      },
      {
        h2: "Why your rate may be different",
        body: "Your actual rate also depends on: loan term (longer = higher), down payment (lower = higher), vehicle age (older = higher), debt-to-income ratio, and prior auto loan history.",
      },
      {
        h2: "Three ways to lock a lower rate",
        body: "1. **Get pre-approved with 2–3 lenders** so the dealer competes.\n2. **Time it right** — apply for credit increases 60 days before, not just before, your auto loan.\n3. **Use a co-signer** if your score is below 660 — can drop your rate by 2–4 points.", // noqa: partner-count
      },
    ],
    faqs: [
      {
        q: "How often do auto loan rates change?",
        a: "Lenders update rates weekly based on prime rate movements. Major shifts follow Federal Reserve announcements.",
      },
      {
        q: "Is it worth waiting for rates to drop?",
        a: "Generally no — if you need the car now. You can always refinance later when rates fall.",
      },
    ],
  },
];

export const findGuide = (slug) => GUIDES.find((g) => g.slug === slug);

// Mapping legacy short-form categories ("Loans", "Buying") to the
// canonical taxonomy used by the GuidesIndex page so the static guides
// merge cleanly into the same chip filter set as DB-stored articles.
const CATEGORY_REMAP = {
  Loans: "Auto Loans",
  Buying: "Car Buying",
  Insurance: "Auto Insurance",
  Warranty: "Car Warranties",
};

// Per-slug hero images for the 3 static guides (which live as hardcoded
// React routes, not DB rows). Each slug → branded cluster OG. Iter-200
// audit: replaced legacy Unsplash photos so the og:image + schema.org
// /article image surfaces a CarSavr-branded asset rather than a generic
// stock photo (better SERP / social-share CTR). These are not rendered
// as a visible page hero by GuidePage.jsx — only fed into og:image +
// JSON-LD — so identical art across 3 sibling guides is acceptable.
const STATIC_IMG = {
  "lease-vs-buy": "https://carsavr.com/og/auto-loans.png",
  "how-to-lower-car-payment": "https://carsavr.com/og/auto-loans.png",
  "car-loan-rates": "https://carsavr.com/og/auto-loans.png",
};

// Per-slug alt text for the 3 static guides (a11y + SEO).
const STATIC_ALT = {
  "lease-vs-buy":
    "Car keys representing the lease vs. buy decision",
  "how-to-lower-car-payment":
    "Two people reviewing finance paperwork with laptops on how to lower a car payment",
  "car-loan-rates":
    "Financial chart representing current auto loan rates",
};

// Last-resort fallback when a static guide is added without a corresponding
// per-slug entry above. The render layer also has an onError fallback in
// ArticlesIndex so a broken Unsplash URL never shows a broken-image icon.
const FALLBACK_IMG = "https://carsavr.com/og-default.png";

/**
 * Iter-122 (Sprint D / L-2) — Per-guide hero-image accessor used by the
 * `GuidePage` <SEO image> prop so each guide ships a guide-specific
 * og:image instead of the generic /og-default.png. Falls back to the
 * shared FALLBACK_IMG when a slug lacks a static entry (parity with the
 * grid-card rendering path).
 */
export const getGuideHeroImage = (slug) => STATIC_IMG[slug] || FALLBACK_IMG;

export const getGuideHeroAlt = (slug, category, title) =>
  STATIC_ALT[slug] || `${category} guide: ${title}`;

/**
 * Return the static GUIDES list shaped like the article-API response,
 * with an extra `route_path` field that points to the actual hardcoded
 * route (e.g. `/lease-vs-buy`) instead of the `/guides/{slug}` default.
 *
 * Used by `ArticlesIndex` so static guides appear alongside DB-stored
 * articles in the same grid + filter chips. Routes are unchanged — the
 * card just links to the legacy hardcoded route via `route_path`.
 *
 * Skips entries whose slug overlaps with an already-defined route
 * (`best-auto-loan-lenders` is rendered by the standalone BestAutoLoans
 * page, not via GuidePage). Without this guard the same listing would
 * appear twice in the merged grid.
 */
// Iter-196.Q — SKIP_SLUGS extended after editorial system shipped 250+
// API-driven guides. The 3 legacy hardcoded routes below
// (`/how-to-lower-car-payment`, `/lease-vs-buy`, `/car-loan-rates`)
// still serve direct SEO traffic via the legacy `GuidePage` component,
// but they no longer get merged into `/guides` so the hero count
// stays honest (250 published articles → milestone hit).
const SKIP_SLUGS = new Set([
  "best-auto-loan-lenders",
  "how-to-lower-car-payment",
  "lease-vs-buy",
  "car-loan-rates",
]);

export const guidesAsArticles = () =>
  GUIDES.filter((g) => !SKIP_SLUGS.has(g.slug)).map((g) => {
    const category = CATEGORY_REMAP[g.category] || g.category;
    return {
      slug: g.slug,
      title: g.title,
      excerpt: g.description,
      category,
      read_time: g.read_time,
      image_url: STATIC_IMG[g.slug] || FALLBACK_IMG,
      image_alt: STATIC_ALT[g.slug] || `${category} guide: ${g.title}`,
      byline: "CarSavr Editorial Team",
      published_at: "2025-11-01",
      // The marker that tells the ArticlesIndex card to link to the
      // legacy hardcoded route instead of `/guides/{slug}`.
      route_path: `/${g.slug}`,
    };
  });

